// src/services/data/fetchers/tokenFetchers.ts
/**
 * Async fetchers for token data.
 * Used inside React Query's queryFn where hooks cannot be used.
 * 
 * These parallel the hooks in useTokenMetrics.ts but are pure async functions.
 */

import { 
  fetchTokensCurrent,
  fetchTokensCurrentRatios,
  fetchTokensChanges,
  fetchTokensRollups,
  fetchTokensHistoryPoints,
  fetchTokensHistoryRatios,
} from '@/services/queries/tokens/tokensScreenerQueries';
import { 
  alignSeriesToCommonDates, 
  extractWindowedValue,
  type TimeSeriesData,
} from './transformations';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface MetricSpec {
  key: string;
  transform?: 'change' | 'rollup';
  window?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const isRatioMetric = (key: string) => key.startsWith('ratio:');

// ─────────────────────────────────────────────────────────────────────────────
// Current Values
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch current metric values for tokens.
 * Handles ratio metrics, change transforms, and rollup transforms.
 */
export async function fetchTokenCurrentValues(
  tickers: string[],
  metricSpec: MetricSpec
): Promise<Record<string, number>> {
  if (tickers.length === 0) return {};
  
  const tickersCsv = tickers.join(',');
  const { key, transform, window } = metricSpec;
  const isRatio = isRatioMetric(key);
  const result: Record<string, number> = {};

  // Handle windowed transforms
  if (transform === 'change' && window) {
    const response = await fetchTokensChanges({ tickersCsv, windowsCsv: window });
    for (const t of response.tokens) {
      result[t.ticker] = extractWindowedValue(t.metrics, key, window);
    }
    return result;
  }

  if (transform === 'rollup' && window) {
    const response = await fetchTokensRollups({ tickersCsv, windowsCsv: window });
    for (const t of response.tokens) {
      result[t.ticker] = extractWindowedValue(t.metrics, key, window);
    }
    return result;
  }

  // Point metrics (current values)
  const response = isRatio
    ? await fetchTokensCurrentRatios({ tickersCsv })
    : await fetchTokensCurrent({ tickersCsv });

  for (const t of response.tokens) {
    result[t.ticker] = t.metrics[key] ?? 0;
  }
  
  return result;
}

/**
 * Fetch multiple metrics for tokens in parallel.
 * Returns a map of ticker -> metric -> value.
 */
export async function fetchTokenMultipleValues(
  tickers: string[],
  metricSpecs: MetricSpec[]
): Promise<Record<string, Record<string, number>>> {
  if (tickers.length === 0 || metricSpecs.length === 0) return {};

  const results = await Promise.all(
    metricSpecs.map(spec => fetchTokenCurrentValues(tickers, spec))
  );

  // Combine into ticker -> metrics map
  const combined: Record<string, Record<string, number>> = {};
  
  for (const ticker of tickers) {
    combined[ticker] = {};
    for (let i = 0; i < metricSpecs.length; i++) {
      const metricKey = metricSpecs[i]!.key;
      combined[ticker][metricKey] = results[i]![ticker] ?? 0;
    }
  }

  return combined;
}

// ─────────────────────────────────────────────────────────────────────────────
// Historical Data
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch historical time series for multiple tokens.
 * Returns aligned dates and series.
 */
export async function fetchTokenHistoricalData(
  tickers: string[],
  metricKey: string
): Promise<TimeSeriesData> {
  if (tickers.length === 0) return { dates: [], series: [] };

  const tickersCsv = tickers.join(',');
  const isRatio = isRatioMetric(metricKey);

  const results = isRatio
    ? await fetchTokensHistoryRatios({ tickersCsv, ratioKeysCsv: metricKey })
    : await fetchTokensHistoryPoints({ tickersCsv, metricKeysCsv: metricKey });

  if (!results?.length) return { dates: [], series: [] };

  // Extract per-token data
  const seriesData = results.map(result => {
    const metric = result.metrics.find(m => m.metric_key === metricKey);
    return {
      name: result.ticker,
      dates: (metric?.dates ?? []) as (string | number)[],
      values: (metric?.values ?? []).map(v => v ?? null),
    };
  });

  return alignSeriesToCommonDates(seriesData);
}

